import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, FlatList } from 'react-native';
import { useLazyListAllFeatureFlagsQuery } from '@/store/api/admin/featureFlagsApi';
import { FeatureFlagAttributes } from '@/types/admin/featureFlags';
import { Accordion } from '@/components';
import { FeatureFlagsSkeleton } from './FeatureFlagsSkeleton';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useFeatureFlagsData } from './hooks/useFeatureFlagsData';
import { useOrganizationFlagsEditor } from './hooks/useOrganizationFlagsEditor';
import { useOrgDotColor } from './utils/featureFlagsUtils';
import { PropertyFlagRow } from './components/PropertyFlagRow';

interface FeatureFlagsListProps {
  searchTerm: string;
  onSelectRow: (flag: FeatureFlagAttributes, isOrgView: boolean) => void;
}

export const FeatureFlagsList: React.FC<FeatureFlagsListProps> = ({ searchTerm, onSelectRow }) => {
  const { redesign } = useAdminColors();
  const getOrgDotColor = useOrgDotColor();
  const [getFeatureFlags, { data, isLoading }] = useLazyListAllFeatureFlagsQuery();
  const [forceOpenIncrement, setForceOpenIncrement] = useState(0);

  const { organizationSections } = useFeatureFlagsData({ data, searchTerm });
  const { handleEditOrganization } = useOrganizationFlagsEditor(onSelectRow);

  useEffect(() => {
    getFeatureFlags();
  }, [getFeatureFlags]);

  const hasActiveSearch = useMemo(() => !!searchTerm.trim(), [searchTerm]);

  useEffect(() => {
    if (hasActiveSearch) {
      setForceOpenIncrement((prev) => prev + 1);
    } else {
      setForceOpenIncrement(0);
    }
  }, [hasActiveSearch]);

  if (isLoading) {
    return <FeatureFlagsSkeleton />;
  }

  if (!data?.response || data.response.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={[styles.emptyTitle, { color: redesign.text.secondary }]}>
          No Feature Flags
        </Text>
        <Text style={[styles.emptySubtitle, { color: redesign.text.secondary }]}>
          No feature flags have been created yet.
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <FlatList
        style={styles.list}
        contentContainerStyle={styles.listContent}
        data={organizationSections}
        keyExtractor={(item) => item.orgName}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode="on-drag"
        renderItem={({ item }) => {
          const { orgName, flags } = item;
          const count = flags.length;
          const forceOpen = count > 0 ? forceOpenIncrement : undefined;

          return (
            <Accordion
              forceOpen={forceOpen}
              openWhen={hasActiveSearch && count > 0}
              closeWhen={!hasActiveSearch || (hasActiveSearch && count === 0)}
              header={(
                <View style={styles.orgHeaderLeft}>
                  <View style={[styles.orgDot, { backgroundColor: getOrgDotColor(orgName) }]} />
                  <Text
                    style={[styles.orgName, { color: redesign.text.primary }]}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  >
                    {orgName}
                  </Text>
                  <Text style={[styles.orgCount, { color: redesign.text.secondary }]}>
                    {count}
                  </Text>
                </View>
              )}
              headerRightComponent={(
                <TouchableOpacity
                  style={styles.editOrgButton}
                  onPress={() => handleEditOrganization(flags)}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  activeOpacity={0.6}
                >
                  <SVG_COMPONENTS.PENCIL
                    width={16}
                    height={16}
                    color={redesign.text.primary}
                  />
                </TouchableOpacity>
              )}
            >
              {flags.map((flag) => (
                <PropertyFlagRow
                  key={`${flag.organization_id}-${flag.property_id || flag.id}`}
                  flag={flag}
                  onPress={() => onSelectRow(flag, false)}
                />
              ))}
            </Accordion>
          );
        }}
        showsVerticalScrollIndicator={false}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  list: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: 2,
    paddingTop: 8,
    paddingBottom: 32,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '500',
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
  },
  orgHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  orgDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
    flexShrink: 0,
  },
  orgName: {
    fontSize: 18,
    fontWeight: '600',
    marginRight: 12,
    flex: 1,
  },
  orgCount: {
    fontSize: 14,
    flexShrink: 0,
  },
  editOrgButton: {
    padding: 8,
    borderRadius: 4,
  },
});
